import {doc, updateDoc} from 'firebase/firestore';
import {db} from '../firebase';
import {syncSuperAdminPreferences} from './onboardingService';

const STORAGE_PREFIX = 'calliope_super_admin_preferences_';

export interface SuperAdminPreferences {
  mockEhrMode: boolean;
}

export class SuperAdminPreferenceError extends Error {
  rollbackSucceeded: boolean;
  cause: unknown;

  constructor(message: string, cause: unknown, rollbackSucceeded: boolean) {
    super(message);
    this.name = 'SuperAdminPreferenceError';
    this.cause = cause;
    this.rollbackSucceeded = rollbackSucceeded;
  }
}

const normalize = (value: any): SuperAdminPreferences => ({
  mockEhrMode: value?.mockEhrMode === true,
});

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

export function readCachedSuperAdminPreferences(
  userId: string | null | undefined,
): SuperAdminPreferences {
  if (!userId) return {mockEhrMode: false};
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw ? normalize(JSON.parse(raw)) : {mockEhrMode: false};
  } catch {
    return {mockEhrMode: false};
  }
}

export function cacheSuperAdminPreferences(
  userId: string,
  preferences: SuperAdminPreferences,
): void {
  try {
    localStorage.setItem(
      storageKey(userId),
      JSON.stringify(normalize(preferences)),
    );
  } catch {
    // The in-memory context remains authoritative when storage is unavailable.
  }
}

export function clearCachedSuperAdminPreferences(
  userId: string | null | undefined,
): void {
  if (!userId) return;
  try {
    localStorage.removeItem(storageKey(userId));
  } catch {
    // Storage may be unavailable; context state is still cleared on sign-out.
  }
}

export function resolveSuperAdminPreferences(
  role: string | undefined,
  userId: string,
  firestoreValue: any,
  backendValue?: any,
): SuperAdminPreferences {
  if (role !== 'superadmin') return {mockEhrMode: false};
  const remote = firestoreValue ?? backendValue;
  if (remote && typeof remote.mockEhrMode === 'boolean') {
    const resolved = normalize(remote);
    cacheSuperAdminPreferences(userId, resolved);
    return resolved;
  }
  return readCachedSuperAdminPreferences(userId);
}

async function persist(
  userId: string,
  preferences: SuperAdminPreferences,
): Promise<void> {
  const normalized = normalize(preferences);
  // Firestore is the cross-platform source of truth. The backend mirror keeps
  // API-only clients compatible, but a rollout lag there must not undo a
  // successful privileged Firestore write.
  await updateDoc(doc(db, 'User', userId), {
    'superAdminPreferences.mockEhrMode': normalized.mockEhrMode,
  });
  try {
    await syncSuperAdminPreferences(normalized);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Super-admin preference backend mirror failed:', error);
    }
  }
}

export async function saveSuperAdminPreferences(
  userId: string,
  next: SuperAdminPreferences,
  previous: SuperAdminPreferences,
): Promise<void> {
  cacheSuperAdminPreferences(userId, next);
  try {
    await persist(userId, next);
  } catch (cause) {
    cacheSuperAdminPreferences(userId, previous);
    let rollbackSucceeded = false;
    try {
      await persist(userId, previous);
      rollbackSucceeded = true;
    } catch {
      // The caller still receives a typed error and has already rolled UI back.
    }
    throw new SuperAdminPreferenceError(
      'Could not save Mock EHR Mode.',
      cause,
      rollbackSucceeded,
    );
  }
}

export default {
  readCachedSuperAdminPreferences,
  cacheSuperAdminPreferences,
  clearCachedSuperAdminPreferences,
  resolveSuperAdminPreferences,
  saveSuperAdminPreferences,
};
